import React, { useState, useEffect } from 'react';
import ThreeHeartBackground from './components/ThreeHeartBackground';
import PetalsRain from './components/PetalsRain';
import Timeline from './components/Timeline';
import TopControlPanel from './components/TopControlPanel';
import ControlPanel from './components/ControlPanel';
import DetailPanel from './components/DetailPanel';
import AddNodePanel from './components/AddNodePanel';
import QuestionModal from './components/QuestionModal';
import { apiClient, Node as ApiNode } from './api/client';
import './App.css';

/**
 * TimelineNode 类型定义
 * 
 * 字段说明：
 * - id: 节点唯一标识
 * - date: 日期
 * - title: 标题
 * - description: 详细描述
 * - icon: 图标类型
 * - perspective: 视角（男/女）
 * - stage: 阶段（相识、相知、相恋、相守）
 * - attachments: 附件数组（图片、视频、音频）
 */

export type TimelineNode = {
  id: string;
  date: string;
  title: string;
  description: string;
  icon: 'user' | 'heart' | 'love' | 'star' | 'landmark' | 'calendar';
  perspective: 'male' | 'female';
  stage?: string;
  heart_rate?: number; // 该节点对应的心跳速率
  attachments?: Array<{
    type: 'image' | 'video' | 'audio';
    url: string;
    caption?: string;
  }>;
};

/**
 * 示例时间轴节点数据
 * 包含 6 个重要时刻，涵盖相识、相知、相恋、相守四个阶段
 */

const sampleNodes: TimelineNode[] = [
  {
      "id": "ffb4e1ef-875c-4761-89a5-f82ff08fdfcf",
      "date": "2026-01-24T14:00",
      "title": "小爱同学",
      "description": "有一个真人版的小爱同学咯",
      "icon": "love",
      "perspective": "female",
      "stage": "相恋",
      "heart_rate": 100
  },
  {
      "id": "7c854b74-1d71-4730-b802-01cc82ef4ee6",
      "date": "2026-01-24T07:22",
      "title": "一次勇敢",
      "description": "一些神奇的魔力让我开始上头，并经过长久的准备开始尝试勇敢，幸运的是这次我是幸运的，我的勇敢得到了仁慈的回应，自此我开始变得幸运！（我喜欢上你要比你早一点点哦）",
      "icon": "love",
      "perspective": "male",
      "stage": "相恋",
      "heart_rate": 124
  },
  {
      "id": "7f04b063-f4f1-4104-a6d7-e1d3185fd348",
      "date": "2026-01-15T22:33",
      "title": "一次转折",
      "description": "这一天的你很累，到了晚上情绪开始崩溃，你坦诚了多次哭泣，不知为何听到你不开心会控制不住的想来找你。\n\n最终就是用深夜惊吓（可能）缓解了你的情绪，同时收获了与你的第一个正式拥抱。",
      "icon": "heart",
      "perspective": "male",
      "stage": "相知",
      "heart_rate": 92
  },
  {
      "id": "4b43f0da-e84d-4bbe-b3f1-a6e687f10282",
      "date": "2025-11-23T20:24",
      "title": "一次长思",
      "description": "这一天我们聊了一些当时的我认为太过深入的话题，你的观点和想法让我沉思了很久，也让我明确了自己真实的想法，可能就是从此喜欢上了鲜明的你吧",
      "icon": "calendar",
      "perspective": "male",
      "stage": "相知",
      "heart_rate": 60
  },
  {
      "id": "daf15453-cce3-4cd1-a587-6a46862487fa",
      "date": "2025-10-17T18:00",
      "title": "见面了",
      "description": "长沙杉杉奥特莱斯广场·吃饭皇帝大·308号桌\n一身黑的你优雅从容，从此多了一个饭搭子",
      "icon": "landmark",
      "perspective": "male",
      "stage": "相识",
      "heart_rate": 78
  },
  {
      "id": "831730fd-3ce4-4958-8ec4-912b21a87c51",
      "date": "2025-10-11T14:06",
      "title": "开始",
      "description": "我通过了你的朋友验证请求，现在我们可以开始聊天了",
      "icon": "user",
      "perspective": "male",
      "stage": "相识",
      "heart_rate": 72
  }
];

/**
 * App 主组件
 * 
 * 功能：
 * - 管理应用状态（心跳速率、自动模式、选中的节点、详情面板显示）
 * - 集成所有子组件
 * - 处理键盘事件（ESC 关闭详情面板）
 * - 使用 IntersectionObserver 监测时间轴节点可见性
 * 
 * 状态管理：
 * - heart_rate: 当前心跳速率（默认 72 BPM）
 * - autoMode: 自动心率模式开关
 * - selectedNode: 当前选中的时间轴节点
 * - showDetail: 详情面板显示状态
 * 
 * 交互逻辑：
 * - 滚动时间轴时自动高亮当前可见节点
 * - 点击节点显示详情面板
 * - 自动模式下滚动或点击节点会切换心跳速率
 * - 非自动模式下仅高亮节点，心跳速率保持不变
 */

const App: React.FC = () => {
  const [heartRate, setHeartRate] = useState(72); // 当前心跳速率，默认 72 BPM
  const [autoMode, setAutoMode] = useState(true); // 自动心率模式开关
  const [selectedNode, setSelectedNode] = useState<TimelineNode | null>(null); // 当前选中的时间轴节点
  const [showDetail, setShowDetail] = useState(false); // 详情面板显示状态
  const [isPlaying, setIsPlaying] = useState(false); // 背景音乐播放状态
  const [showAddNode, setShowAddNode] = useState(false); // 新增节点面板显示状态
  const [editingNode, setEditingNode] = useState<TimelineNode | null>(null); // 正在编辑的节点
  const [nodes, setNodes] = useState<TimelineNode[]>([]); // 时间轴节点列表
  const [isLoading, setIsLoading] = useState(true); // 加载状态
  const [error, setError] = useState<string | null>(null); // 错误信息
  const [warning, setWarning] = useState<string | null>(null); // 警告信息
  const [permission, setPermission] = useState<'full' | 'view' | null>(null); // 用户权限状态
  const [showQuestion, setShowQuestion] = useState(true); // 是否显示问题模态框
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null); // Toast 提示

  /**
   * 显示 Toast 提示
   */
  const showToast = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 3000);
  };

/**
   * 从 API 加载节点数据
   * 初始加载时不包含描述和附件，通过验证后再加载完整数据
   * 如果加载失败，则使用 localStorage 数据作为降级方案
   * 如果 localStorage 也没有数据，则使用 sampleNodes
   */
  useEffect(() => {
    const loadNodes = async () => {
      setIsLoading(true);
      setError(null);
      setWarning(null);
      
      try {
        const response = await apiClient.getAllNodes();
        
        if (response.success && response.data && response.data.length > 0) {
          // API 加载成功，只加载基本信息（不包含描述和附件）
          const timelineNodes: TimelineNode[] = response.data.map((apiNode: ApiNode) => ({
            id: apiNode.id,
            date: apiNode.date,
            title: apiNode.title,
            description: '', // 初始为空，验证通过后再加载
            icon: apiNode.icon,
            perspective: apiNode.perspective,
            stage: apiNode.stage,
            heart_rate: apiNode.heart_rate,
            attachments: undefined // 初始为空，验证通过后再加载
          }));
          
          // 保存完整数据到 localStorage 作为备份
          localStorage.setItem('timelineNodes', JSON.stringify(response.data.map((apiNode: ApiNode) => ({
            id: apiNode.id,
            date: apiNode.date,
            title: apiNode.title,
            description: apiNode.description,
            icon: apiNode.icon,
            perspective: apiNode.perspective,
            stage: apiNode.stage,
            heart_rate: apiNode.heart_rate,
            attachments: apiNode.attachments ? apiNode.attachments.map(att => ({
              type: att.type,
              url: att.url,
              caption: att.caption
            })) : undefined
          }))));
          
          setNodes(timelineNodes);
        } else {
          // API 返回空数据或失败，尝试使用 localStorage
          throw new Error(response.error || 'API 返回空数据');
        }
      } catch (err) {
        console.error('API 加载节点失败，尝试使用 localStorage:', err);
        
        // 尝试从 localStorage 加载数据
        const savedNodes = localStorage.getItem('timelineNodes');
        if (savedNodes) {
          try {
            const parsedNodes: TimelineNode[] = JSON.parse(savedNodes);
            if (parsedNodes.length > 0) {
              // 从 localStorage 加载时只加载基本信息（描述和附件）
              // 但不清空 localStorage 中的数据，以便后续验证通过后可以使用
              const basicNodes: TimelineNode[] = parsedNodes.map(node => ({
                ...node,
                description: '',
                attachments: undefined
              }));
              setNodes(basicNodes);
              setWarning('使用本地缓存数据');
              return;
            }
          } catch (error) {
            console.error('解析 localStorage 数据失败:', error);
          }
        }
        
        // localStorage 也没有数据，使用 sampleNodes
        console.log('使用示例数据');
        // 注意：sampleNodes 中的描述和附件会被清空，因为示例数据不应该包含敏感信息
        const basicSampleNodes: TimelineNode[] = sampleNodes.map(node => ({
          ...node,
          description: '',
          attachments: undefined
        }));
        setNodes(basicSampleNodes);
        setWarning('使用示例数据（网络连接失败）');
      } finally {
        setIsLoading(false);
      }
    };

    loadNodes();
  }, []);

  /**
   * 加载完整的节点数据（描述和附件）
   * 在用户通过验证后调用
   */
  const loadFullNodeData = async () => {
    try {
      const response = await apiClient.getAllNodes();
      
      if (response.success && response.data && response.data.length > 0) {
        const fullNodes: TimelineNode[] = response.data.map((apiNode: ApiNode) => ({
          id: apiNode.id,
          date: apiNode.date,
          title: apiNode.title,
          description: apiNode.description,
          icon: apiNode.icon,
          perspective: apiNode.perspective,
          stage: apiNode.stage,
          heart_rate: apiNode.heart_rate,
          attachments: apiNode.attachments ? apiNode.attachments.map(att => ({
            type: att.type,
            url: att.url,
            caption: att.caption
          })) : undefined
        }));
        
        setNodes(fullNodes);
      }
    } catch (err) {
      console.error('加载完整数据失败:', err);
      // API 加载失败时不从 localStorage 加载，保持基本信息
      showToast('加载详细数据失败，请联系管理员', 'error');
    }
  };

  /**
   * 保存节点数据到 localStorage（备份）
   */
  const saveNodesToLocalStorage = (nodesToSave: TimelineNode[]) => {
    try {
      localStorage.setItem('timelineNodes', JSON.stringify(nodesToSave));
    } catch (error) {
      console.error('保存到 localStorage 失败:', error);
    }
  };

  /**
   * 背景音乐播放控制
   */
  const handleMusicToggle = () => {
    const audioElement = document.getElementById('background-music') as HTMLAudioElement;
    
    if (!audioElement) {
      showToast('背景音乐加载失败', 'error');
      return;
    }

    if (isPlaying) {
      audioElement.pause();
    } else {
      audioElement.play().catch(err => {
        console.error('播放背景音乐失败:', err);
        showToast('播放背景音乐失败', 'error');
      });
    }
    
    setIsPlaying(!isPlaying);
  };

  /**
   * 新增节点控制
   */
  const handleAddNode = () => {
    if (permission === null) {
      // 未验证时点击新增节点，弹出问题验证
      setShowQuestion(true);
      return;
    }
    if (permission !== 'full') {
      showToast('您当前只有浏览权限，无法添加节点', 'warning');
      return;
    }
    setShowAddNode(true);
    console.log('打开新增节点面板');
  };

  /**
   * 验证身份
   */
  const handleVerifyIdentity = () => {
    setShowQuestion(true);
  };

  /**
   * 保存新节点
   * 优先保存到 API，如果失败则保存到 localStorage
   */
  const handleSaveNode = async (newNode: TimelineNode) => {
    try {
      // 先上传附件
      let uploadedAttachments: typeof newNode.attachments = [];
      
      if (newNode.attachments && newNode.attachments.length > 0) {
        for (const attachment of newNode.attachments) {
          // 从 blob URL 获取文件
          const response = await fetch(attachment.url);
          const blob = await response.blob();
          const file = new File([blob], attachment.caption || 'file', {
            type: attachment.type === 'image' ? 'image/*' : 
                  attachment.type === 'video' ? 'video/*' : 'audio/*'
          });

          // 上传到 API
          const uploadResponse = await apiClient.uploadAttachment(
            newNode.id,
            file,
            attachment.caption
          );

          if (uploadResponse.success && uploadResponse.data) {
            uploadedAttachments.push(uploadResponse.data);
          }
        }
      }

      const { attachments, ...nodeData } = newNode;

      if (editingNode) {
        // 编辑模式：更新节点
        const updateResponse = await apiClient.updateNode(editingNode.id, {
          date: nodeData.date,
          title: nodeData.title,
          description: nodeData.description,
          icon: nodeData.icon,
          perspective: nodeData.perspective,
          stage: nodeData.stage,
          heart_rate: nodeData.heart_rate
        });

        if (updateResponse.success && updateResponse.data) {
          // 更新节点列表
          const updatedNodes = nodes.map(n => n.id === newNode.id ? newNode : n);
          setNodes(updatedNodes);
          saveNodesToLocalStorage(updatedNodes);
          console.log('更新节点到 API:', newNode);
          showToast('节点更新成功！');
        } else {
          throw new Error(updateResponse.error || 'API 更新失败');
        }
      } else {
        // 新增模式：创建节点
        const createResponse = await apiClient.createNode({
          date: nodeData.date,
          title: nodeData.title,
          description: nodeData.description,
          icon: nodeData.icon,
          perspective: nodeData.perspective,
          stage: nodeData.stage,
          heart_rate: nodeData.heart_rate
        });

        if (createResponse.success && createResponse.data) {
          // API 保存成功，更新节点列表
          const updatedNodes = [...nodes, newNode];
          setNodes(updatedNodes);
          saveNodesToLocalStorage(updatedNodes);
          console.log('保存新节点到 API:', newNode);
          showToast('节点保存成功！');
        } else {
          throw new Error(createResponse.error || 'API 保存失败');
        }
      }
    } catch (error) {
      console.error('API 保存节点失败，保存到 localStorage:', error);
      
      // API 保存失败，保存到 localStorage
      let updatedNodes;
      if (editingNode) {
        updatedNodes = nodes.map(n => n.id === newNode.id ? newNode : n);
      } else {
        updatedNodes = [...nodes, newNode];
      }
      setNodes(updatedNodes);
      saveNodesToLocalStorage(updatedNodes);
      
      showToast('节点已保存到本地（网络连接失败，稍后将自动同步）', 'warning');
    }

    setShowAddNode(false);
  };

  /**
   * 关闭新增节点面板
   */
  const handleCloseAddNode = () => {
    setShowAddNode(false);
    setEditingNode(null);
  };

  /**
 * 编辑节点
   */
  const handleEditNode = (node: TimelineNode) => {
    setEditingNode(node);
    setShowAddNode(true); // 直接打开编辑面板，覆盖在详情面板之上
  };

  /**
   * ESC 键关闭详情面板和新增节点面板
   */
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showDetail) {
          setShowDetail(false);
        }
        if (showAddNode) {
          setShowAddNode(false);
          setEditingNode(null);
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showDetail, showAddNode]);

  /**
   * IntersectionObserver 监测时间轴节点可见性
   * 
   * 功能：
   * - 持续监测时间轴节点是否进入视野中心区域
   * - 当节点可见时更新选中状态
   * - 自动模式下同时更新心跳速率
   * 
   * 配置：
   * - threshold: 0.5（节点 50% 可见时触发）
   * - rootMargin: '-20% 0px -20% 0px'（上下各留 20% 缓冲区，避免频繁切换）
   * 
   * 注意：
   * - 此 observer 始终运行，与自动开关无关
   * - 滚动选中节点功能不受自动模式影响
   * - 自动开关仅控制是否自动切换心跳速率
   */
  useEffect(() => {
    /**
   * IntersectionObserver 回调函数
   * 当节点进入视野中心时触发
   */
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // 获取节点 ID
          const nodeId = entry.target.getAttribute('data-node-id');
          // 查找对应的节点数据
          const node = nodes.find(n => n.id === nodeId);
          if (node) {
            // 始终更新选中节点（不受自动模式影响）
            setSelectedNode(node);
            // 只在自动模式下更新心跳速率
            if (autoMode && node.heart_rate) {
                  setHeartRate(node.heart_rate);
                }          }
        }
      });
    },
    {
      threshold: 0.5, // 节点 50% 可见时触发
      rootMargin: '-20% 0px -20% 0px' // 上下各留 20% 缓冲区
    }
  );

    // 观察所有带有 data-node-id 属性的元素
    const nodeElements = document.querySelectorAll('[data-node-id]');
    nodeElements.forEach((el) => observer.observe(el));

    // 组件卸载时断开 observer
    return () => {
      observer.disconnect();
    };
  }, [nodes, autoMode]);

  /**
   * 节点点击处理函数
   * 
   * 功能：
   * - 更新选中节点
   * - 显示详情面板
   * - 自动模式下更新心跳速率
   */
  /**
   * 节点点击处理函数
   * 
   * 功能：
   * - 更新选中节点
   * - 显示详情面板（需要通过验证）
   * - 自动模式下更新心跳速率
   */
  const handleNodeClick = (node: TimelineNode) => {
    if (permission === null) {
      showToast('请先完成身份验证', 'warning');
      return;
    }
    
    // 设置选中节点并显示详情面板
    setSelectedNode(node);
    setShowDetail(true);
    // 只在自动模式下点击节点时更新心跳速率
    if (autoMode && node.heart_rate) {
      setHeartRate(node.heart_rate);
    }
  };

  /**
   * 关闭详情面板
   */
  const handleCloseDetail = () => {
    setShowDetail(false);
  };

  /**
   * 渲染应用界面
   * 
   * 结构：
   * - ThreeHeartBackground: 3D 心形粒子背景（全屏）
   * - app-content: 主内容区域
   *   - Timeline: 交互式时间轴
   *   - ControlPanel: 心跳速率控制面板
   * - DetailPanel: 详情面板（条件渲染）
   */
  return (
      <div className="app">
        {/* 3D 心形粒子背景 */}
        <ThreeHeartBackground heartRate={heartRate} />
        
        {/* 玫瑰花瓣雨 */}
        <PetalsRain heartRate={heartRate} />
        
        {/* 主内容区域 */}
        <div className="app-content">
          {/* 顶部控制面板 */}
          <TopControlPanel
            isPlaying={isPlaying}
            onMusicToggle={handleMusicToggle}
            onAddNode={handleAddNode}
            onVerifyIdentity={handleVerifyIdentity}
            permissionStatus={permission}
          />
          
          {/* 加载状态 */}
          {isLoading && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <div className="loading-text">加载中...</div>
            </div>
          )}
          
          {/* 警告提示 */}
          {warning && (
            <div className="warning-message">
              <span>{warning}</span>
              <button onClick={() => setWarning(null)}>×</button>
            </div>
          )}
          
          {/* 错误提示 */}
          {error && (
            <div className="error-message">
              {error}
              <button onClick={() => window.location.reload()}>重试</button>
            </div>
          )}
          
          {/* 交互式时间轴 */}
          {!isLoading && !error && nodes.length > 0 && (
            <Timeline nodes={nodes} selectedId={selectedNode?.id} onNodeClick={handleNodeClick} />
          )}
          
          {/* 心跳速率控制面板 */}
          <ControlPanel heartRate={heartRate} onChange={setHeartRate} autoMode={autoMode} onAutoModeChange={setAutoMode} />
        </div>
        
        {/* 详情面板（条件渲染） */}
        {showDetail && selectedNode && (
          <DetailPanel 
            node={selectedNode} 
            onClose={handleCloseDetail}
            canEdit={permission === 'full'}
            onEdit={handleEditNode}
            permission={permission}
          />
        )}
  
        {/* 新增节点面板（条件渲染） */}
        {showAddNode && (
          <AddNodePanel 
            onClose={handleCloseAddNode} 
            onSave={handleSaveNode}
            editingNode={editingNode}
            showToast={showToast}
          />
        )}

        {/* 问题验证模态框（条件渲染） */}
        {showQuestion && permission !== 'full' && (
          <QuestionModal
            onComplete={async (perm) => {
              if(perm == 'view'){
                document.title = '一些回忆'
              }
              setPermission(perm);
              setShowQuestion(false);
              // 通过验证后加载完整的节点数据（描述和附件）
              await loadFullNodeData();
            }}
          />
        )}

        {/* Toast 提示 */}
        {toast && (
          <div className={`toast toast-${toast.type}`}>
            {toast.message}
          </div>
        )}
      </div>
    );};

export default App;